import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Clock, ArrowRight, X, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentPending() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [remaining, setRemaining] = useState(null);
  const [status, setStatus] = useState("loading"); // loading, pending, expired, paid
  const [bookingInfo, setBookingInfo] = useState({});
  const [cancelling, setCancelling] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    checkTimeout();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [bookingId]);

  const checkTimeout = async () => {
    try {
      const res = await axios.get(`${API}/bookings/${bookingId}/timeout-status`);
      const data = res.data;

      if (data.status === "paid") {
        setStatus("paid");
        return;
      }
      if (data.status === "expired") {
        setStatus("expired");
        return;
      }

      setStatus("pending");
      setRemaining(data.remaining_seconds);
      setBookingInfo({ outfit_name: data.outfit_name, total_amount: data.total_amount });

      // Start countdown
      if (intervalRef.current) clearInterval(intervalRef.current);
      let secs = data.remaining_seconds;
      intervalRef.current = setInterval(() => {
        secs -= 1;
        if (secs <= 0) {
          clearInterval(intervalRef.current);
          setStatus("expired");
          setRemaining(0);
        } else {
          setRemaining(secs);
        }
      }, 1000);
    } catch {
      setStatus("expired");
    }
  };

  const handleRetryPayment = async () => {
    try {
      const res = await axios.post(`${API}/payments/create-session`, {
        booking_id: bookingId,
        origin_url: window.location.origin
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      if (err.response?.data?.detail?.includes("already paid")) {
        setStatus("paid");
      } else {
        toast.error(err.response?.data?.detail || "Unable to resume payment");
      }
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/bookings/${bookingId}/cancel-pending`);
      toast.success("Booking cancelled — item released");
      navigate("/catalog");
    } catch {
      toast.error("Failed to cancel");
    } finally {
      setCancelling(false);
    }
  };

  const formatTime = (secs) => {
    if (!secs || secs < 0) return "0:00";
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const pct = remaining != null ? Math.max(0, (remaining / 600) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#FAF8F0] relative">
      <div className="petal-left hidden lg:block" />
      <div className="petal-right hidden lg:block" />
      <Navbar />

      <div className="px-6 py-20 lg:py-28">
        <div className="max-w-lg mx-auto text-center">

          {status === "loading" && (
            <div className="animate-fadeIn" data-testid="payment-pending-loading">
              <Loader2 className="w-10 h-10 animate-spin text-[#9A7B4F] mx-auto mb-6" />
              <p className="text-[#676664]/50 font-light">Checking payment status...</p>
            </div>
          )}

          {status === "pending" && (
            <div className="animate-fadeIn" data-testid="payment-pending-active">
              {/* Timer Circle */}
              <div className="relative w-32 h-32 mx-auto mb-8">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="54" fill="none" stroke="#282828" strokeOpacity="0.06" strokeWidth="6" />
                  <circle cx="60" cy="60" r="54" fill="none" stroke="#9A7B4F" strokeWidth="6"
                    strokeDasharray={`${2 * Math.PI * 54}`}
                    strokeDashoffset={`${2 * Math.PI * 54 * (1 - pct / 100)}`}
                    strokeLinecap="round"
                    className="transition-all duration-1000"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <Clock className="w-4 h-4 text-[#9A7B4F] mb-1" />
                  <span className="text-2xl font-mono text-[#282828]" data-testid="countdown-timer">{formatTime(remaining)}</span>
                </div>
              </div>

              <p className="text-[#9A7B4F] text-[11px] tracking-[0.3em] uppercase mb-3">Payment Pending</p>
              <h1 className="text-3xl text-[#676664] mb-3">Complete Your Payment</h1>
              <p className="text-[#676664]/50 font-light mb-2 text-sm">
                Your booking for <span className="text-[#282828] font-medium">{bookingInfo.outfit_name}</span> is reserved.
              </p>
              <p className="text-[#676664]/40 font-light mb-8 text-xs">
                You have <span className="text-[#9A7B4F] font-medium">{formatTime(remaining)}</span> to complete payment before the item is released back to our collection.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={handleRetryPayment}
                  className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-10 py-6 text-[11px] tracking-[0.15em] uppercase"
                  data-testid="retry-payment-btn"
                >
                  Complete Payment — ${bookingInfo.total_amount?.toFixed(2)} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button variant="outline" onClick={handleCancel} disabled={cancelling}
                  className="border-[#282828]/15 text-[#676664] hover:bg-[#282828]/5 px-8 py-6 text-[11px] tracking-[0.15em] uppercase"
                  data-testid="cancel-booking-btn"
                >
                  {cancelling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
                  Cancel & Release
                </Button>
              </div>
            </div>
          )}

          {status === "expired" && (
            <div className="animate-fadeIn" data-testid="payment-expired">
              <div className="w-20 h-20 bg-[#f5e1e0] flex items-center justify-center mx-auto mb-8">
                <AlertTriangle className="w-8 h-8 text-[#C4A4A0]" strokeWidth={1.5} />
              </div>
              <p className="text-[#C4A4A0] text-[11px] tracking-[0.3em] uppercase mb-3">Session Expired</p>
              <h1 className="text-3xl text-[#676664] mb-4">Payment Time Expired</h1>
              <p className="text-[#676664]/50 font-light mb-8 text-sm">
                The 10-minute payment window has closed and the item has been released back to our collection. You can start a new booking if it's still available.
              </p>
              <Link to="/catalog">
                <Button className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-10 py-6 text-[11px] tracking-[0.15em] uppercase" data-testid="browse-collection-btn">
                  Browse Collection <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          )}

          {status === "paid" && (
            <div className="animate-fadeIn" data-testid="payment-already-paid">
              <div className="w-20 h-20 bg-[#D4B8B4] flex items-center justify-center mx-auto mb-8">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <h1 className="text-3xl text-[#676664] mb-4">Payment Already Confirmed</h1>
              <p className="text-[#676664]/50 font-light mb-8">Your booking is confirmed. Check your email for details.</p>
              <Link to="/catalog">
                <Button className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-10 py-6 text-[11px] tracking-[0.15em] uppercase">
                  Continue Browsing <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
